import { UploadFileProps } from '@anm/components/InputFile';
import { ListItem } from '@anm/components/list/SelectionList';
import { getLogosList } from '@anm/helpers/branding';
import isNil from 'lodash/isNil';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { brandActions, brandSelectors } from '../../../../../../store/modules/brands';
import { landingsActions, landingsSelectors } from '../../../../../../store/modules/landings';
import { userPrefsActions } from '../../../../../../store/modules/userprefs';

const LOGO_OFFSET_INDEX = 2;

export const useBrandLogo = () => {
  const dispatch = useDispatch();
  const logoList = useSelector(brandSelectors.selectBrandLogoList);
  const brandId = useSelector(brandSelectors.selectCurrentBrandId);
  const isPendingUploadLogo = useSelector(brandSelectors.selectIsPendingUploadLogo);
  const isPendingDeleteLogo = useSelector(brandSelectors.selectIsPendingDeleteLogo);
  const isPending = isPendingUploadLogo || isPendingDeleteLogo;

  const landing = useSelector(landingsSelectors.selectLanding);
  const brandLogosList = getLogosList(logoList);
  const logoUrl = landing?.parameters?.logoUrl;

  const [logoIndex, setLogoIndex] = useState<number>(0);

  useEffect(() => {
    if (!logoList || !logoUrl) return;

    const logoIndex = logoList?.findIndex(l => l.preview === logoUrl);

    if (logoIndex >= 0) {
      setLogoIndex(logoIndex + LOGO_OFFSET_INDEX);
    }
  }, [logoList, logoUrl]); // TODO: move selected frame to first one after upload logo

  const onLogoChange = useCallback(
    ({ file }: UploadFileProps) => {
      brandId && dispatch(brandActions.uploadBrandLogo({ file, brandId }));
    },
    [brandId]
  );

  const updateLandingLogo = useCallback(
    (logoUrl: string) =>
      dispatch(
        landingsActions.updateLanding({
          ...landing,
          parameters: {
            ...landing?.parameters,
            logoUrl
          }
        })
      ),
    [landing]
  );

  const onListChange = useCallback(
    (item: ListItem) => {
      if (isNil(item?.iconSrc)) return;

      if (!item?.iconSrc) {
        setLogoIndex(0);
      }

      updateLandingLogo(item.iconSrc);
    },
    [landing, logoIndex]
  );

  return [brandLogosList, logoIndex, isPending, onLogoChange, onListChange] as const;
};

export const useBrandList = () => {
  const dispatch = useDispatch();
  const brandList = useSelector(brandSelectors.selectBrands);
  const brandId = useSelector(brandSelectors.selectCurrentBrandId);

  const onBrandChange = useCallback((brandId: number) => {
    dispatch(userPrefsActions.updatePreferredBrand(brandId));
    dispatch(brandActions.updateCurrentBrandId(brandId));
  }, []);

  return [brandList, brandId, onBrandChange] as const;
};
